import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormControlLabel, Radio } from "@mui/material";
import { format } from "date-fns";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { Checkbox as CustomCheckbox } from "components/ui/Checkbox";
import { WEIGHT_MEASUREMENT_HINT } from "features/cdm/constants/formOptions";
import { onInvalidSubmit } from "utils/reportFormErrors";

import { ItemFormWrapper } from "../ItemFormWrapper/ItemFormWrapper";
import style from "./ReviewAndExamForm.module.css";

const formSchema = z.object({
  height: z
    .string()
    .min(1, "Height is required")
    .refine((val) => {
      const num = Number(val);
      return !isNaN(num) && num >= 50 && num <= 250;
    }, "Height must be between 50 and 250 cm"),
  weight: z
    .string()
    .min(1, "Weight is required")
    .refine((val) => {
      const num = Number(val);
      return !isNaN(num) && num >= 20 && num <= 500;
    }, "Weight must be between 20 and 500 kg"),
  waistCircumference: z
    .string()
    .min(1, "Waist circumference is required")
    .refine((val) => {
      const num = Number(val);
      return !isNaN(num) && num >= 50 && num <= 250;
    }, "Waist circumference must be between 50 and 250 cm"),
  pulse: z
    .string()
    .min(1, "Pulse is required")
    .refine((val) => {
      const num = Number(val);
      return !isNaN(num) && num >= 20 && num <= 200;
    }, "Pulse must be between 20 and 200 bpm"),
  pulseRhythm: z.string().optional().nullable(),
  bp1: z
    .string()
    .min(1, "BP is required")
    .refine((val) => {
      const num = Number(val);
      return !isNaN(num) && num >= 50 && num <= 250;
    }, "Systolic BP must be between 50 and 250 mmHg"),
  bp2: z
    .string()
    .min(1, "BP is required")
    .refine((val) => {
      const num = Number(val);
      return !isNaN(num) && num >= 30 && num <= 180;
    }, "Diastolic BP must be between 30 and 180 mmHg"),
  bmi: z.string().min(1, "BMI is required"),
});
export const ReviewAndExamForm = ({
  onBack,
  onContinue,
  data,
  isReview,
  isOCF,
  isPP,
  baselineData,
}) => {
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      height: data?.height,
      weight: data?.weight,
      waistCircumference: data?.waistCircumference,
      pulse: data?.pulse,
      pulseRhythm: data?.pulseRhythm || "REGULAR",
      bp1: data?.bp1,
      bp2: data?.bp2,
      bmi: data?.bmi,
    },
  });

  useEffect(() => {
    if (!baselineData) return;
    const [baselineBp1, baselineBp2] = baselineData.bloodPressure?.split("/") ?? [];
    const prefill = {};
    if (!form.getValues("height") && baselineData.heightCm)
      prefill.height = String(baselineData.heightCm);
    // Weight must NOT carry forward between reviews — it has to be re-measured
    // each visit (height, being stable, does carry forward).
    if (!form.getValues("waistCircumference") && baselineData.waistCm)
      prefill.waistCircumference = String(baselineData.waistCm);
    if (!form.getValues("bp1") && baselineBp1) prefill.bp1 = baselineBp1.trim();
    if (!form.getValues("bp2") && baselineBp2) prefill.bp2 = baselineBp2.trim();
    if (Object.keys(prefill).length > 0)
      form.reset({ ...form.getValues(), ...prefill });
  }, [baselineData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFormSubmit = (values) => {
    onContinue({ ...values, pulseRhythm: values.pulseRhythm || "IRREGULAR" });
  };

  const height = form.watch("height");
  const weight = form.watch("weight");

  useEffect(() => {
    if (height && weight) {
      const heightInMeters = height / 100;

      form.setValue("bmi", (weight / Math.pow(heightInMeters, 2)).toFixed(2));
    }
  }, [height, weight, form]);

  return (
    <ItemFormWrapper
      onBack={onBack}
      onContinue={form.handleSubmit(handleFormSubmit, onInvalidSubmit("ReviewAndExamForm"))}
      isReview={isReview}
    >
      <div className={style.itemWrapper}>
        <h3>Review details</h3>
        <div className={style.reviewDetails}>
          <div>
            <h5>Registered</h5>
            <p>{format(data.registeredDate, "dd-MM-yyyy")}</p>
          </div>
          <div>
            <h5>Reviewing Date</h5>
            <p>{format(data.registeredDate, "dd-MM-yyyy")}</p>
          </div>
          <div>
            <h5>Reviewing Doctor</h5>
            <p>{data.reviewingDoctor}</p>
          </div>
          <div>
            <h5>Created by</h5>
            <p>{data.createdBy}</p>
          </div>
        </div>
      </div>
      {data?.diagnosis && !isOCF && (
        <div className={style.itemWrapper}>
          <h3>
            {isPP ? "Reason" : "Diagnosis"} - <span>{data.diagnosis}</span>
          </h3>
        </div>
      )}
      {data?.indications && data?.indications?.length > 0 && isOCF && (
        <div className={style.itemWrapper}>
          <h3>Indications</h3>
          <ul className={style.indicationsList}>
            {data.indications.map((indication) => (
              <li key={indication} className={style.indicationItem}>
                <CustomCheckbox checked />
                <p>{indication}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
      {data?.reasonForReview && !isOCF && (
        <div className={style.itemWrapper}>
          <h3>
            {isPP ? "Reason" : "Diagnosis"} -{" "}
            <span>{data.reasonForReview}</span>
          </h3>
        </div>
      )}
      <div
        className={style.itemWrapper}
        style={{
          marginBottom: "0px",
          paddingBottom: "0px",
          borderBottom: "none",
        }}
      >
        <h3>Physical Examination</h3>
        <FormProvider {...form}>
          <form
            className={style.addReferralForm}
            onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("ReviewAndExamForm"))}
          >
            <div
              className={style.inputGroupGrid4Columns}
              style={{ marginBottom: "12px" }}
            >
              <FormField name="height">
                <label className={style.addReferralFormLabel}>Height</label>
                <div className={style.cmInput}>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="form-control"
                    disabled={isReview}
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/[^0-9.]/g, "");
                      const num = parseFloat(e.target.value);
                      if (!isNaN(num) && num > 250) e.target.value = "250";
                    }}
                    {...form.register("height")}
                  />
                </div>
              </FormField>
              <FormField name="weight">
                <label className={style.addReferralFormLabel}>Weight</label>
                <div className={style.kgInput}>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="form-control"
                    disabled={isReview}
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/[^0-9.]/g, "");
                      const num = parseFloat(e.target.value);
                      if (!isNaN(num) && num > 500) e.target.value = "500";
                    }}
                    {...form.register("weight")}
                  />
                </div>
                <p className={style.fieldHint}>{WEIGHT_MEASUREMENT_HINT}</p>
              </FormField>
              <FormField name="bmi">
                <label className={style.addReferralFormLabel}>BMI</label>
                <input
                  className="form-control"
                  disabled
                  {...form.register("bmi")}
                />
              </FormField>
              <FormField name="waistCircumference">
                <label className={style.addReferralFormLabel}>
                  Waist Circumference
                </label>
                <div className={style.cmInput}>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="form-control"
                    disabled={isReview}
                    onInput={(e) =>
                      (e.target.value = e.target.value.replace(/[^0-9.]/g, ""))
                    }
                    {...form.register("waistCircumference")}
                  />
                </div>
              </FormField>
            </div>
            <div className={style.examSecondRow}>
              <FormField name="pulse">
                <label className={style.addReferralFormLabel}>Pulse Rate</label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="form-control"
                  disabled={isReview}
                  onInput={(e) =>
                    (e.target.value = e.target.value.replace(/[^0-9]/g, ""))
                  }
                  {...form.register("pulse")}
                />
              </FormField>
              <FormField name="pulseRhythm">
                <label className={style.addReferralFormLabel}>
                  Pulse Rhythm
                </label>
                <div className={style.radioGroup}>
                  <FormControlLabel
                    value="REGULAR"
                    control={
                      <Radio
                        disabled={isReview}
                        size="small"
                        checked={form.watch("pulseRhythm") === "REGULAR"}
                        onChange={(e) => {
                          form.setValue("pulseRhythm", e.target.value);
                        }}
                        sx={{
                          "&.Mui-checked": {
                            color: "var(--green-900)",
                          },
                        }}
                      />
                    }
                    label="Regular"
                  />
                  <FormControlLabel
                    value="IRREGULAR"
                    control={
                      <Radio
                        disabled={isReview}
                        size="small"
                        checked={form.watch("pulseRhythm") === "IRREGULAR"}
                        onChange={(e) => {
                          form.setValue("pulseRhythm", e.target.value);
                        }}
                        sx={{
                          "&.Mui-checked": {
                            color: "var(--green-900)",
                          },
                        }}
                      />
                    }
                    label="Irregular"
                  />
                </div>
              </FormField>
              <FormField name="bp1">
                <label className={style.addReferralFormLabel}>
                  Systolic Blood Pressure
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="form-control"
                  disabled={isReview}
                  onInput={(e) =>
                    (e.target.value = e.target.value.replace(/[^0-9]/g, ""))
                  }
                  {...form.register("bp1")}
                />
              </FormField>
              <FormField name="bp2">
                <label className={style.addReferralFormLabel}>
                  Diastolic Blood Pressure
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="form-control"
                  disabled={isReview}
                  onInput={(e) =>
                    (e.target.value = e.target.value.replace(/[^0-9]/g, ""))
                  }
                  {...form.register("bp2")}
                />
              </FormField>
            </div>
          </form>
        </FormProvider>
      </div>
    </ItemFormWrapper>
  );
};
